import moment from 'moment';
import { push } from 'connected-react-router';
import { change, getFormValues } from 'redux-form';
import { routes } from 'app/routes/route-list';
import {
  REVERSE_ADVISOR_STOP_GATE,
  REVERSE_CITIZEN_AND_MARRIAGE,
  SUMMARY_SECTION,
} from 'app/models/sections/constants';
import { getFormType } from 'app/routes/helpers';
import { HecmCalcsRequest, HecmCalcsResponse, HighTechCalcsResponse, ReverseMortgageProgramType } from 'app/api/rates/types';
import { HIGH_TECH_PAYMENT_OPTION_MIN } from 'app/models/options/options';
import { hasCoBorrower, isUsingReverseToPurchase } from 'app/models/fields/conditionals';
import {
  ajaxPostShortForm,
  getNextSection,
  goToSection,
  referToLoanOfficer,
} from 'app/actions/form/actions';
import { FieldNames } from 'app/models/fields/names';
import { fetchHecmCalcs, fetchHighTechCalcs } from 'app/api/rates';
import { getAuditMetadata, makeActionCreator } from 'app/util/actions';
import { Type } from 'app/actions/form/reverse-mortgage/types';
import { UniversalLoanSource, GeneralReferralCode, ReverseMortgageLoanType, ReverseSummaryPdfRequest, ReverseSummaryType } from '@lenderful/domain';
import { getLoanOfficer } from 'app/reducers/loan-officer';
import { reverseSummaryTypeSelector } from 'app/reducers/app-config';
import {
  mapUniversalLoanRequest,
  patchUniversalLoanApplication,
  postUniversalLoanApplication,
} from 'app/api/loan-application/universal/loan';
import { logger } from 'app/util/logger';
import { hasEitherSsn } from '../../../components/FormFields/Ssn/CollectSsnBase';
import { postEmailReverseSummary } from 'app/api/reverse-summary';
import { Dispatch } from 'redux';
import { getDownPayment, getEstimatedHomeValue, getLineOfCredit, getLoanTerm, getMortgageBalance, getTenurePayment, getTermPayment } from 'app/api/loan-application/universal/reverse';
import { ajaxValidateBorrowerResidenceNoNavigate } from '../address-validation/actions';
import { YesOrNo } from '../../../models/options/enums';
import { mapBrokerLoanOfficer } from 'app/api/loan-application/universal/mappings';
import { getSlug } from 'app/util/headers';
import { isBadRequestError } from 'app/util/misc';

export const ajaxReverseMortgageCalcs        = makeActionCreator(Type.AJAX_REVERSE_MORTGAGE_CALCS, 'payload');
export const ajaxReverseMortgageCalcsSuccess = makeActionCreator(Type.AJAX_REVERSE_MORTGAGE_CALCS_SUCCESS, 'payload');
export const ajaxReverseMortgageCalcsFailure = makeActionCreator(Type.AJAX_REVERSE_MORTGAGE_CALCS_FAIL, 'payload');
export const ajaxEmailReverseSummary         = makeActionCreator(Type.AJAX_EMAIL_REVERSE_SUMMARY, 'payload');
export const ajaxEmailReverseSummaryFail     = makeActionCreator(Type.AJAX_EMAIL_REVERSE_SUMMARY_FAIL, 'payload');
export const ajaxEmailReverseSummarySuccess  = makeActionCreator(Type.AJAX_EMAIL_REVERSE_SUMMARY_SUCCESS, 'payload');

/**
 * @param {ReverseMortgageProgramType} [reverseMortgageProgramType] which program (HECM or High Tech) the borrower
 * chose to apply for. @todo map this into the application payload once the contract exists (POC).
 */
export function handleReverseMortgageLoanSelection(reverseMortgageProgramType?: ReverseMortgageProgramType) {
  return (dispatch) => {
    if (reverseMortgageProgramType) {
      dispatch(change(UniversalLoanSource.REVERSE, FieldNames.reverseMortgageProgramType, reverseMortgageProgramType));
    }
    dispatch(change(UniversalLoanSource.REVERSE, FieldNames.hasReverseSummaryInfo, YesOrNo.YES));
    dispatch(push(`${routes.reverse}#${REVERSE_CITIZEN_AND_MARRIAGE}`));
  };
}

export function handleReverseMortgageAskForAdvisor() {
  return (dispatch) => {
    dispatch(push(`${routes.reverse}#${REVERSE_ADVISOR_STOP_GATE}`));
  };
}

export function handleAddressValidationAndReverseShortFormSubmit() {
  return async (dispatch, getState) => {
    const state = getState();
    const formName = getFormType(state.router.location.pathname);
    const formData = getFormValues(formName)(state);
    ajaxValidateBorrowerResidenceNoNavigate(formData);
    return dispatch(handleReverseShortFormSubmit());
  };
}

const isStateNotSupportedError = (error: any): boolean => {
  return error?.response?.status === 400 && error?.response?.data?.error?.code === 'STATE_NOT_SUPPORTED';
};

const getCalcErrorMessage = (error: any): string | undefined => {
  return error?.response?.data?.error?.description;
};

const isHighTechEnabled = (state): boolean => {
  return reverseSummaryTypeSelector(state) === ReverseSummaryType.HECM_AND_HIGH_TECH;
};

const isRejected = (result: PromiseSettledResult<unknown>): result is PromiseRejectedResult => {
  return result.status === 'rejected';
};

/**
 * Submits the reverse mortgage short form: calculates, posts the short form data, then navigates
 * to the summary. A STATE_NOT_SUPPORTED calc error refers the borrower to a loan officer instead.
 *
 * The summary's selections (High Tech payment option, HECM allocation) are defaulted up front so
 * the first calc, and the summary itself, already have something to describe before the borrower
 * has explicitly chosen anything.
 *
 * @returns {Function} thunk
 */
export function handleReverseShortFormSubmit() {
  return async (dispatch, getState) => {
    const state = getState();
    const formName = getFormType(state.router.location.pathname);
    const values = getFormValues(formName)(state);

    if (isHighTechEnabled(state) && values[FieldNames.paymentOption] == null) {
      dispatch(change(formName, FieldNames.paymentOption, HIGH_TECH_PAYMENT_OPTION_MIN));
    }
    if (values[FieldNames.reverseMortgageLoanType] == null) {
      dispatch(change(formName, FieldNames.reverseMortgageLoanType, ReverseMortgageLoanType.LINE_OF_CREDIT));
    }

    try {
      await dispatch(calculateReverseMortgage());
      await dispatch(ajaxPostShortForm());
      return dispatch(goToSection(SUMMARY_SECTION));
    } catch (error) {
      if (isStateNotSupportedError(error)) {
        dispatch(referToLoanOfficer(GeneralReferralCode.STATE_NOT_SUPPORTED));
        return false;
      }
    }
  };
}

const saveHecmResults = (dispatch, formName: string, values, response: HecmCalcsResponse) => {
  dispatch(change(formName, FieldNames.initialMortgageInsurance, response.initialMortgageInsurance));
  dispatch(change(formName, FieldNames.maxClaimAmount, response.maxClaimAmount));
  dispatch(change(formName, FieldNames.maxLumpSum, response.maxLumpSum));
  dispatch(change(formName, FieldNames.netPrincipalLimit, response.netPrincipalLimit));
  dispatch(change(formName, FieldNames.tenurePayment, response.tenurePayment));
  dispatch(change(formName, FieldNames.termPayment, response.termPayment));
  dispatch(change(formName, FieldNames.totalUpfrontCosts, response.totalUpfrontCosts));

  if (isUsingReverseToPurchase(values)) {
    const downPayment = getDownPayment(values[FieldNames.purchasePrice], response.netPrincipalLimit, response.totalUpfrontCosts);
    dispatch(change(formName, FieldNames.downDollar, Math.max(downPayment, 0)));
  }
};

const saveHighTechResults = (dispatch, formName: string, response: HighTechCalcsResponse) => {
  dispatch(change(formName, FieldNames.ltv, response.ltv));
  dispatch(change(formName, FieldNames.cappedPayment, response.cappedPayment));
  dispatch(change(formName, FieldNames.firstMinimumPayment, response.firstMinimumPayment));
  dispatch(change(formName, FieldNames.maxLineOfCredit, response.maxLineOfCredit));
  dispatch(change(formName, FieldNames.minLoanAmount, response.minLoanAmount));
  dispatch(change(formName, FieldNames.maxLoanAmount, response.maxLoanAmount));
};

/**
 * Requests the HECM calc and, when the client shows it, the High Tech calc, then saves the results
 * into the form.
 *
 * The two calcs succeed or fail independently: a failure on one side must not throw away the other
 * side's results, and the summary disables Apply Now for whichever side failed. Only when both fail
 * (or the state isn't supported, which also refers the borrower to a loan officer and throws) does
 * the whole calc count as failed. margin is resolved server-side and is not sent.
 *
 * @returns {Function} thunk
 */
export function calculateReverseMortgage() {
  return async (dispatch, getState) => {

    const state = getState();
    const formName = getFormType(state.router.location.pathname);
    const values = getFormValues(formName)(state);

    const today = moment();

    const borrowerAgeInFiveMonths = values[FieldNames.age] ?
      values[FieldNames.age] :
      today.diff(moment(values[FieldNames.dateOfBirth]).add(5, 'M'), 'years');

    const coBorrowerAgeInFiveMonths = values[FieldNames.coBorrowerAge] ?
      values[FieldNames.coBorrowerAge] :
      today.diff(moment(values[FieldNames.coBorrowerDOB]).add(5, 'M'), 'years');

    const sharedParams = {
      ageOfYoungest      : hasCoBorrower(values) ? Math.min(borrowerAgeInFiveMonths, coBorrowerAgeInFiveMonths) : borrowerAgeInFiveMonths,
      estimatedHomeValue : getEstimatedHomeValue(values),
      mortgageBalance    : getMortgageBalance(values),
      propertyState      : values[FieldNames.propertyState] ? values[FieldNames.propertyState] : values[FieldNames.livingPropertyState],
    };
    const hecmRequest: HecmCalcsRequest = {
      ...sharedParams,
      expectedLumpSum : values[FieldNames.expectedLumpSum],
      term            : values[FieldNames.loanTerm] * 12,
    };

    dispatch(ajaxReverseMortgageCalcs());

    const [hecmResult, highTechResult] = await Promise.allSettled([
      fetchHecmCalcs(hecmRequest),
      isHighTechEnabled(state) ? fetchHighTechCalcs({ ...sharedParams, paymentOption: values[FieldNames.paymentOption] }) : Promise.resolve(undefined),
    ]);

    const rejections = [hecmResult, highTechResult].filter(isRejected);
    rejections.forEach(({ reason }) => logger.error('Error: Reverse Mortgage calcs', reason));

    const stateNotSupported = rejections.find(({ reason }) => isStateNotSupportedError(reason));
    if (stateNotSupported) {
      dispatch(ajaxReverseMortgageCalcsFailure());
      dispatch(referToLoanOfficer(GeneralReferralCode.STATE_NOT_SUPPORTED));
      throw stateNotSupported.reason;
    }

    const hecmFailed     = isRejected(hecmResult);
    const highTechFailed = isRejected(highTechResult);
    const hecmError      = isRejected(hecmResult) ? getCalcErrorMessage(hecmResult.reason) : undefined;
    const highTechError  = isRejected(highTechResult) ? getCalcErrorMessage(highTechResult.reason) : undefined;
    if (hecmFailed && highTechFailed) {
      dispatch(ajaxReverseMortgageCalcsFailure({ hecmError, highTechError }));
      return;
    }
    dispatch(ajaxReverseMortgageCalcsSuccess({ hecmFailed, highTechFailed, hecmError, highTechError }));

    if (!isRejected(hecmResult)) {
      saveHecmResults(dispatch, formName, values, hecmResult.value);
    }
    if (!isRejected(highTechResult) && highTechResult.value) {
      saveHighTechResults(dispatch, formName, highTechResult.value);
    }
  };
}

export function handleReverseFormSubmit() {
  return async (dispatch) => {
    // @todo add handleDeclarationFlagsWhitelist()
    try {
      await dispatch(handleReverseMortgageApplication());
    } catch (error) {
      return; // non-400 failure: stay on this page, error already logged above
    }
    return dispatch(getNextSection());
  };
}

/**
 * This method builds the new payload before submitting to the backend endpoint
 *
 * @param {FormName} formName
 * @param {FieldNames} fieldName
 * @param {string} value
 */
export const handleReverseMortgageApplication = () => {
  return async (dispatch, getState) => {
    const state = getState();
    const formName = getFormType(state.router.location.pathname);
    const formData = getFormValues(formName)(state);
    const loanOfficer = getLoanOfficer(state);

    const universalLoanRequest = mapUniversalLoanRequest(formData, loanOfficer, formName);
    let auditMetaData;
    if (hasEitherSsn()) {
      auditMetaData = getAuditMetadata('Submit button');
    }
    if(formData[FieldNames.universalApplicationId]) {
      try {
        const response = await patchUniversalLoanApplication({ ...universalLoanRequest, auditMetaData });
        logger.debug('Patch Reverse Mortgage Response', response);
        return response;
      } catch (error) {
        logger.error('Error: Patch Reverse Mortgage', error);
        // A 400 means the backend rejected this application; swallow it here so the
        // caller advances to confirmation anyway. Any other failure re-throws so the
        // caller keeps the user on this page.
        if (!isBadRequestError(error)) {
          throw error;
        }
      }
    } else {
      try {
        const response = await postUniversalLoanApplication({ ...universalLoanRequest, auditMetaData });
        logger.debug('Post Reverse Mortgage Response', response);
        return response;
      } catch (error) {
        logger.error('Error: Post Reverse Mortgage', error);
        // A 400 means the backend rejected this application; swallow it here so the
        // caller advances to confirmation anyway. Any other failure re-throws so the
        // caller keeps the user on this page.
        if (!isBadRequestError(error)) {
          throw error;
        }
      }
    }
  };
};

export function handleEmailReverseMortgageSummary() {
  return (dispatch: Dispatch, getState) => {
    const state       = getState();
    const formName    = getFormType(state.router.location.pathname);
    const values      = getFormValues(formName)(state);
    const loanOfficer = getLoanOfficer(state);

    const brokerLoanOfficer       = mapBrokerLoanOfficer(values);
    const mortgageBalance         = getMortgageBalance(values);
    const tenurePayment           = getTenurePayment(values);
    const termPayment             = getTermPayment(values);
    const loanTerm                = getLoanTerm(values);
    const estimatedHomeValue      = getEstimatedHomeValue(values);
    const lineOfCreditAmount      = getLineOfCredit(values);
    const propertyAppraisedValue  = values[FieldNames.homeValue];
    const netPrincipalLimit       = values[FieldNames.netPrincipalLimit];
    const totalUpfrontCosts       = values[FieldNames.totalUpfrontCosts];
    const reverseMortgageLoanType = values[FieldNames.reverseMortgageLoanType];
    const purchasePrice           = values[FieldNames.purchasePrice];
    const expectedLumpSum         = values[FieldNames.expectedLumpSum];
    const downPayment             = getDownPayment(purchasePrice, netPrincipalLimit, totalUpfrontCosts);
    const slug                    = getSlug();

    const remainingEquity = propertyAppraisedValue - mortgageBalance - netPrincipalLimit - totalUpfrontCosts;

    const payload: ReverseSummaryPdfRequest = {
      brokerLoanOfficer,
      downPayment,
      email               : values[FieldNames.email],
      estimatedHomeValue,
      expectedLumpSum,
      firstName           : values[FieldNames.firstName],
      isPurchasingNewHome : isUsingReverseToPurchase(values),
      lastName            : values[FieldNames.lastName],
      lineOfCreditAmount,
      loanOfficerId       : loanOfficer.id,
      loanTerm,
      mortgageBalance,
      netPrincipalLimit,
      propertyStreet      : isUsingReverseToPurchase(values) ? values[FieldNames.propertyStreet] : values[FieldNames.livingPropertyStreet],
      propertyStreet2     : isUsingReverseToPurchase(values) ? values[FieldNames.propertyStreet2] : values[FieldNames.livingPropertyStreet2],
      propertyCity        : isUsingReverseToPurchase(values) ? values[FieldNames.propertyCity] : values[FieldNames.livingPropertyCity],
      propertyState       : isUsingReverseToPurchase(values) ? values[FieldNames.propertyState] : values[FieldNames.livingPropertyState],
      propertyZip         : isUsingReverseToPurchase(values) ? values[FieldNames.propertyZip] : values[FieldNames.livingPropertyZip],
      purchasePrice,
      remainingEquity,
      reverseMortgageLoanType,
      slug,
      tenurePayment,
      termPayment,
      totalUpfrontCosts,
    };
    dispatch({ type: Type.AJAX_EMAIL_REVERSE_SUMMARY, payload });
    return postEmailReverseSummary(payload).then(
      (response) => {
        dispatch(ajaxEmailReverseSummarySuccess(response));
      },
      (error) => {
        dispatch(ajaxEmailReverseSummaryFail(error));
      });
  };
}
